import { logReportDownload } from "../../util/auditLogger";
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from "react";
import { collection, onSnapshot, query, limit } from "firebase/firestore";
import { db } from "../../util/firebase";
import { executeApi } from "../../util/apiHelper";
import Context from "../../Context";
import {
  Users,
  Activity,
  Search,
  RefreshCw,
  Laptop,
  Radio,
  Layers,
  AlertTriangle,
  Compass,
  MonitorCheck,
  Sparkles,
  FileText,
  Eye,
  X,
  History,
  ShieldAlert,
  Download,
  Clock,
  Globe,
  MapPin,
  ExternalLink,
  Phone,
  Crosshair,
} from "lucide-react";

// Route name mapping for friendly display
const ROUTE_NAMES = {
  "/": "หน้าหลัก (Home)",
  "/dashboard": "แดชบอร์ดสรุปผล",
  "/orders": "จัดการคำสั่งซื้อ (Orders)",
  "/orders/list": "รายการคำสั่งซื้อทั้งหมด",
  "/coupons": "จัดการคูปองและโปรโมชั่น",
  "/coupons/manage": "รายการคูปอง",
  "/coupons/orders": "ออเดอร์คูปอง",
  "/users": "จัดการข้อมูลลูกค้า/ผู้ใช้",
  "/admins": "จัดการแอดมินและสิทธิ์",
  "/lotteries": "จัดการสลากและงวด",
  "/lotteries/imported": "สลากที่นำเข้าแล้ว",
  "/lotteries/unimported": "สลากที่ยังไม่นำเข้า",
  "/prizes": "ตรวจรางวัลและโอนเงินรางวัล",
  "/withdraw": "ระบบถอนเงินตัวแทน",
  "/reports": "รายงานสรุปยอดขาย",
  "/settings": "ตั้งค่าระบบ",
  "/dev": "Dev Tools",
  "/dev/monitor": "Dev Real-time Admin Monitor",
  "/dev/all-in-one-draw": "All-in-One ปิดงวด",
  "/admin-center": "Admin Center & Live Orders",
  "/complains": "ข้อร้องเรียนและการแจ้งปัญหา",
  "/donates": "ระบบบริจาค",
  "/missions": "ภารกิจและของรางวัล",
};

const getFriendlyRouteName = (path) => {
  if (!path) return "ไม่ระบุ";
  const cleanPath = path.split("?")[0];
  if (ROUTE_NAMES[cleanPath]) return ROUTE_NAMES[cleanPath];

  for (const [key, label] of Object.entries(ROUTE_NAMES)) {
    if (key !== "/" && cleanPath.startsWith(key)) {
      return `${label} (${cleanPath})`;
    }
  }
  return cleanPath;
};

const formatTimeAgo = (timestamp, msFallback) => {
  const targetMs =
    msFallback ||
    (timestamp?.toMillis ? timestamp.toMillis() : timestamp?.toDate ? timestamp.toDate().getTime() : typeof timestamp === "number" ? timestamp : 0);

  if (!targetMs) return "ไม่ทราบเวลา";
  const diffSec = Math.max(0, Math.floor((Date.now() - targetMs) / 1000));

  if (diffSec < 10) return "เมื่อสักครู่";
  if (diffSec < 60) return `${diffSec} วินาทีที่แล้ว`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} นาทีที่แล้ว`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour} ชม. ที่แล้ว`;
  return `${Math.floor(diffHour / 24)} วันที่แล้ว`;
};

const calculateEffectiveStatus = (admin) => {
  if (admin.status === "offline") return "offline";
  const lastActiveMs =
    admin.lastActiveMs ||
    (admin.lastActive?.toMillis ? admin.lastActive.toMillis() : 0) ||
    0;

  if (!lastActiveMs) return admin.status || "offline";
  const diffSec = (Date.now() - lastActiveMs) / 1000;

  if (diffSec > 15 * 60) return "offline";
  if (diffSec > 3 * 60 || admin.status === "idle") return "idle";
  return "online";
};

const getRoleBadge = (role) => {
  switch (role) {
    case "dev":
      return { label: "Developer", bg: "bg-violet-50 text-violet-700 border-violet-200" };
    case "superAdmin":
      return { label: "Super Admin", bg: "bg-amber-50 text-amber-800 border-amber-200" };
    case "admin":
      return { label: "Admin", bg: "bg-blue-50 text-blue-700 border-blue-200" };
    case "superScanner":
      return { label: "Super Scanner", bg: "bg-emerald-50 text-emerald-800 border-emerald-200" };
    case "scanner":
      return { label: "Scanner", bg: "bg-teal-50 text-teal-700 border-teal-200" };
    case "inventory":
      return { label: "Inventory", bg: "bg-orange-50 text-orange-800 border-orange-200" };
    case "superAccount":
      return { label: "Super Accountant", bg: "bg-indigo-50 text-indigo-800 border-indigo-200" };
    case "account":
      return { label: "Accountant", bg: "bg-cyan-50 text-cyan-700 border-cyan-200" };
    case "marketing":
      return { label: "Marketing", bg: "bg-purple-50 text-purple-700 border-purple-200" };
    default:
      return { label: role || "Staff", bg: "bg-gray-100 text-gray-700 border-gray-200" };
  }
};

const getTypeBadge = (type) => {
  switch (type) {
    case "orders":
      return { label: "คำสั่งซื้อ (Orders)", bg: "bg-emerald-50 text-emerald-700 border-emerald-200" };
    case "users":
      return { label: "ผู้ใช้งาน (Users)", bg: "bg-blue-50 text-blue-700 border-blue-200" };
    case "configs":
      return { label: "ตั้งค่าระบบ (Configs)", bg: "bg-purple-50 text-purple-700 border-purple-200" };
    case "coupons":
      return { label: "คูปอง (Coupons)", bg: "bg-amber-50 text-amber-700 border-amber-200" };
    case "missions":
      return { label: "กิจกรรม/ภารกิจ (Missions)", bg: "bg-yellow-50 text-yellow-800 border-yellow-200" };
    case "donations":
      return { label: "บริจาค (Donations)", bg: "bg-pink-50 text-pink-700 border-pink-200" };
    case "withdraw":
      return { label: "การถอนเงิน (Withdraw)", bg: "bg-lime-50 text-lime-800 border-lime-200" };
    case "admins":
      return { label: "สิทธิ์แอดมิน (Admins)", bg: "bg-indigo-50 text-indigo-700 border-indigo-200" };
    case "marketing":
      return { label: "การตลาด/ลิงก์ (Marketing)", bg: "bg-cyan-50 text-cyan-800 border-cyan-200" };
    case "complains":
      return { label: "ข้อร้องเรียน (Complains)", bg: "bg-rose-50 text-rose-700 border-rose-200" };
    case "points":
      return { label: "แต้มคะแนน (Points)", bg: "bg-indigo-50 text-indigo-700 border-indigo-200" };
    case "lotteries":
      return { label: "สลาก (Lotteries)", bg: "bg-cyan-50 text-cyan-700 border-cyan-200" };
    case "prizes":
      return { label: "รางวัล (Prizes)", bg: "bg-orange-50 text-orange-700 border-orange-200" };
    case "bads":
      return { label: "ระงับ/แบล็คลิสต์ (Bads)", bg: "bg-red-50 text-red-700 border-red-200" };
    case "reports":
      return { label: "รายงาน (Reports)", bg: "bg-teal-50 text-teal-700 border-teal-200" };
    case "search":
      return { label: "การค้นหา (Search)", bg: "bg-sky-50 text-sky-700 border-sky-200" };
    case "navigation":
    case "page_view":
      return { label: "เข้าหน้าเว็บ (Navigation)", bg: "bg-violet-50 text-violet-700 border-violet-200" };
    case "link_click":
      return { label: "คลิกลิงก์ (Link Click)", bg: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200" };
    case "dev":
      return { label: "Dev Tools", bg: "bg-zinc-100 text-zinc-800 border-zinc-300" };
    case "system":
      return { label: "ระบบ (System)", bg: "bg-slate-100 text-slate-700 border-slate-200" };
    default:
      return { label: type || "ทั่วไป", bg: "bg-slate-100 text-slate-700 border-slate-200" };
  }
};

export const DevMonitor = () => {
  const { profileState } = Context();
  const [activeTab, setActiveTab] = useState("presence"); // 'presence' | 'logs'
  const [admins, setAdmins] = useState([]);
  const [systemLogs, setSystemLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRole, setSelectedRole] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedLogType, setSelectedLogType] = useState("all");
  const [selectedLogDetail, setSelectedLogDetail] = useState(null);
  const [selectedAdminForHistory, setSelectedAdminForHistory] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now());

  const currentUserId = String(profileState?.userId || profileState?._id || profileState?.username || "");
  const isAuthorized = String(profileState?.userId) === "201989";

  // Tick clock every 10s to update relative times smoothly
  useEffect(() => {
    if (!isAuthorized) return;
    const timer = setInterval(() => {
      setNowTick(Date.now());
    }, 10000);
    return () => clearInterval(timer);
  }, [isAuthorized]);

  // 1. Realtime subscription to AdminPresence
  useEffect(() => {
    if (!isAuthorized || !db) return;

    setLoading(true);
    const unsub = onSnapshot(
      collection(db, "AdminPresence"),
      (snapshot) => {
        const list = [];
        snapshot.forEach((docSnap) => {
          list.push({ id: docSnap.id, ...docSnap.data() });
        });

        // Sort: Current user first, then Online > Idle > Offline, then by lastActiveMs
        list.sort((a, b) => {
          const isMeA = Boolean(currentUserId && String(a.userId || a.id) === currentUserId);
          const isMeB = Boolean(currentUserId && String(b.userId || b.id) === currentUserId);
          if (isMeA && !isMeB) return -1;
          if (!isMeA && isMeB) return 1;

          const statusA = calculateEffectiveStatus(a);
          const statusB = calculateEffectiveStatus(b);
          const priority = { online: 3, idle: 2, offline: 1 };
          const pA = priority[statusA] || 0;
          const pB = priority[statusB] || 0;
          if (pA !== pB) {
            return pB - pA;
          }
          const timeA = a.lastActiveMs || (a.lastActive?.toMillis ? a.lastActive.toMillis() : 0) || 0;
          const timeB = b.lastActiveMs || (b.lastActive?.toMillis ? b.lastActive.toMillis() : 0) || 0;
          return timeB - timeA;
        });

        setAdmins(list);
        setLoading(false);
      },
      (error) => {
        console.warn("Error fetching AdminPresence:", error);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [isAuthorized, currentUserId]);

  // Legacy logs are private: refresh through the authenticated API only while visible.
  useEffect(() => {
    if (!isAuthorized) return;
    let cancelled = false, running = false;
    const refresh = async () => {
      if (running || document.hidden) return;
      running = true;
      try {
        const result = await executeApi("logs-getAll", {limit:60});
        if (!cancelled && Array.isArray(result)) setSystemLogs(result);
      } finally { running = false; if (!cancelled) setLogsLoading(false); }
    };
    setLogsLoading(true);
    refresh();
    const timer = setInterval(refresh, 60000);
    document.addEventListener("visibilitychange", refresh);
    return () => { cancelled = true; clearInterval(timer); document.removeEventListener("visibilitychange", refresh); };
  }, [isAuthorized, currentUserId]);

  // 3. In-App Anomaly Detection (ตรวจจับพฤติกรรมผิดปกติแสดงในหน้าจอ)
  const detectedAnomalies = useMemo(() => {
    const anomalies = [];

    // Check 1: Off-hours activity (00:00 - 05:00 AM)
    systemLogs.forEach((log) => {
      const logTime = log.createdAt ? new Date(log.createdAt) : null;
      if (logTime) {
        const hours = logTime.getHours();
        if (hours >= 0 && hours < 5) {
          anomalies.push({
            id: `off-hours-${log.id}`,
            title: `ทำรายการช่วงวิกาล (${logTime.toLocaleTimeString("th-TH")})`,
            detail: `${log.name || "Admin"} เรียกคำสั่ง ${log.action || "-"} (${log.type || "-"})`,
          });
        }
      }
    });

    // Check 2: High Frequency Action Bursts (>8 actions in 2 minutes)
    const actorGroups = {};
    systemLogs.forEach((log) => {
      const actorKey = log.actor || log.name || "unknown";
      if (!actorGroups[actorKey]) actorGroups[actorKey] = [];
      actorGroups[actorKey].push(log);
    });

    Object.entries(actorGroups).forEach(([actor, list]) => {
      if (list.length >= 8) {
        const newest = list[0]?.createdAt || 0;
        const eighth = list[7]?.createdAt || 0;
        if (newest - eighth < 2 * 60 * 1000 && newest > 0) {
          anomalies.push({
            id: `burst-${actor}-${newest}`,
            title: `ยิงคำสั่งถี่ผิดปกติ (>8 ครั้งใน 2 นาที)`,
            detail: `ผู้ทำรายการ ${list[0].name || actor} (#${actor}) ดำเนินการคำสั่งอย่างรวดเร็วต่อเนื่อง`,
          });
        }
      }
    });

    return anomalies.slice(0, 4);
  }, [systemLogs]);

  // Filtered Admins List
  const filteredAdmins = useMemo(() => {
    return admins.filter((admin) => {
      const status = calculateEffectiveStatus(admin);
      const queryStr = searchQuery.trim().toLowerCase();
      const nameMatch =
        !queryStr ||
        (admin.name || "").toLowerCase().includes(queryStr) ||
        (admin.userId || "").toLowerCase().includes(queryStr) ||
        (admin.phone || "").toLowerCase().includes(queryStr) ||
        (admin.tel || "").toLowerCase().includes(queryStr) ||
        (admin.lineTitle || "").toLowerCase().includes(queryStr) ||
        (admin.lineName || "").toLowerCase().includes(queryStr) ||
        (admin.currentPath || "").toLowerCase().includes(queryStr);

      const roleMatch = selectedRole === "all" || admin.role === selectedRole;
      const statusMatch = selectedStatus === "all" || status === selectedStatus;

      return nameMatch && roleMatch && statusMatch;
    });
  }, [admins, searchQuery, selectedRole, selectedStatus, nowTick]);

  // Filtered System Logs List
  const filteredLogs = useMemo(() => {
    return systemLogs.filter((log) => {
      const queryStr = searchQuery.trim().toLowerCase();
      const matchText =
        !queryStr ||
        (log.name || "").toLowerCase().includes(queryStr) ||
        String(log.actor || "").toLowerCase().includes(queryStr) ||
        (log.action || "").toLowerCase().includes(queryStr) ||
        (log.type || "").toLowerCase().includes(queryStr) ||
        (log.ip || "").toLowerCase().includes(queryStr) ||
        JSON.stringify(log).toLowerCase().includes(queryStr);

      const matchType = selectedLogType === "all" || log.type === selectedLogType;

      return matchText && matchType;
    });
  }, [systemLogs, searchQuery, selectedLogType]);

  // Presence Statistics
  const stats = useMemo(() => {
    let onlineCount = 0;
    let idleCount = 0;
    let offlineCount = 0;
    const pageCounts = {};

    admins.forEach((admin) => {
      const status = calculateEffectiveStatus(admin);
      if (status === "online") {
        onlineCount++;
        const p = admin.currentPath?.split("?")[0] || "/";
        pageCounts[p] = (pageCounts[p] || 0) + 1;
      } else if (status === "idle") {
        idleCount++;
      } else {
        offlineCount++;
      }
    });

    const sortedPages = Object.entries(pageCounts).sort((a, b) => b[1] - a[1]);

    return {
      total: admins.length,
      online: onlineCount,
      idle: idleCount,
      offline: offlineCount,
      topPages: sortedPages,
    };
  }, [admins, nowTick]);

  const uniqueRoles = useMemo(() => {
    const roles = new Set(admins.map((a) => a.role).filter(Boolean));
    return Array.from(roles);
  }, [admins]);

  const uniqueLogTypes = useMemo(() => {
    const types = new Set(systemLogs.map((l) => l.type).filter(Boolean));
    return Array.from(types);
  }, [systemLogs]);

  // CSV Export Function
  const handleExportLogsCSV = () => {
    if (filteredLogs.length === 0) return;
    const headers = ["Timestamp", "Date", "Actor ID", "Admin Name", "Type", "Action", "Page / Target", "Detail", "IP Address", "User Agent"];
    const rows = filteredLogs.map((log) => {
      const dateStr = log.createdAt ? new Date(log.createdAt).toISOString() : "";
      const summary = log.detail || (log.body ? JSON.stringify(log.body) : log.configs ? JSON.stringify(log.configs) : log.target || "-");
      return [
        log.createdAt || "",
        `"${dateStr}"`,
        `"${log.actor || ""}"`,
        `"${log.name || ""}"`,
        `"${log.type || ""}"`,
        `"${log.action || ""}"`,
        `"${(log.page || log.target || "").replace(/"/g, '""')}"`,
        `"${summary.replace(/"/g, '""')}"`,
        `"${log.ip || ""}"`,
        `"${(log.userAgent || "").replace(/"/g, '""')}"`,
      ].join(",");
    });

    const csvContent = "\uFEFF" + [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `system_logs_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    logReportDownload();
    link.click();
    document.body.removeChild(link);
  };

  if (!isAuthorized) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[65vh] p-6 text-center">
        <div className="w-16 h-16 rounded-md bg-rose-50 text-rose-600 flex items-center justify-center mb-4 border border-rose-200 shadow-sm">
          <ShieldAlert className="w-8 h-8" />
        </div>
        <h2 className="text-xl font-bold text-slate-800 mb-1.5">403 เข้าถึงหน้านี้ไม่ได้</h2>
        <p className="text-sm font-semibold text-slate-500 max-w-md">
          หน้านี้ถูกจำกัดสิทธิ์เฉพาะ Dev (User ID: 201989) เท่านั้น
        </p>
      </div>
    );
  }

  return (
    <div className="w-full mx-auto space-y-3.5 font-sans text-slate-800">
      {/* Top Header Card */}
      <div className="bg-white border border-slate-200/90 rounded-md p-3 sm:p-4 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-rose-50 border border-rose-100 rounded-md text-rose-600">
            <MonitorCheck className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-lg font-bold text-slate-900 tracking-tight">
                Dev Real-time Admin & System Monitor
              </h1>
              {/* Real-time Indicator (Red / Rose Standard) */}
              <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-rose-50 border border-rose-200 text-rose-600 text-xs font-semibold shadow-xs">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-rose-500"></span>
                </span>
                LIVE MONITOR
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              เฝ้าติดตามสถานะแอดมิน ประวัติการเข้าหน้า และการบันทึกประวัติการทำงานของระบบ (Audit Logs) แบบ Real-time
            </p>
          </div>
        </div>

        {/* Action Controls & Clock */}
        <div className="flex items-center gap-2 self-start md:self-auto flex-wrap">
          <div className="text-right hidden sm:block mr-2">
            <div className="text-[10px] text-slate-400 font-medium">เวลาปัจจุบัน</div>
            <div className="text-xs font-mono font-semibold text-slate-700">
              {new Date(nowTick).toLocaleTimeString("th-TH")}
            </div>
          </div>

          <button
            onClick={handleExportLogsCSV}
            className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 rounded-md text-xs font-semibold transition-colors shadow-xs"
            title="ดาวน์โหลด Logs เป็นไฟล์ CSV"
          >
            <Download className="w-3.5 h-3.5 text-slate-500" />
            <span>Export CSV</span>
          </button>

          <button
            onClick={() => setNowTick(Date.now())}
            className="flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-200 rounded-md text-xs font-semibold transition-colors shadow-xs"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading || logsLoading ? "animate-spin text-rose-500" : ""}`} />
            <span>ซิงค์ข้อมูล</span>
          </button>
        </div>
      </div>

      {/* In-App Anomaly Alert Banner (เมื่อตรวจพบพฤติกรรมผิดปกติ) */}
      {detectedAnomalies.length > 0 && (
        <div className="bg-amber-50/80 border border-amber-200 rounded-md p-2.5 sm:p-3 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-2.5">
          <div className="flex items-start gap-2.5">
            <div className="p-1.5 bg-amber-100 text-amber-700 rounded-md flex-shrink-0 mt-0.5">
              <ShieldAlert className="w-4 h-4" />
            </div>
            <div className="space-y-0.5">
              <div className="flex items-center gap-2">
                <span className="font-bold text-xs text-amber-900">
                  ตรวจพบพฤติกรรมที่ควรตรวจสอบ ({detectedAnomalies.length} รายการ)
                </span>
                <span className="px-1.5 py-0.2 bg-amber-200/80 text-amber-800 text-[10px] rounded-md font-bold">
                  In-App Anomaly
                </span>
              </div>
              <div className="flex items-center gap-3 text-xs text-amber-800 flex-wrap">
                {detectedAnomalies.map((anom) => (
                  <span key={anom.id} className="inline-flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-600"></span>
                    <span>{anom.title} ({anom.detail})</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {/* 1. Online Count */}
        <div className="bg-white border border-slate-200/90 rounded-md p-3 flex items-center justify-between shadow-sm">
          <div className="space-y-0.5">
            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
              แอดมินออนไลน์ (Active)
            </p>
            <div className="flex items-baseline gap-1.5">
              <span className="text-xl font-bold text-emerald-600">{stats.online}</span>
              <span className="text-xs text-slate-500">คน</span>
            </div>
            <p className="text-[11px] text-emerald-600 flex items-center gap-1 font-medium">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
              กำลังใช้งานระบบอยู่ตอนนี้
            </p>
          </div>
          <div className="p-2.5 bg-emerald-50 border border-emerald-100 rounded-md text-emerald-600">
            <Radio className="w-4 h-4" />
          </div>
        </div>

        {/* 2. Idle Count */}
        <div className="bg-white border border-slate-200/90 rounded-md p-3 flex items-center justify-between shadow-sm">
          <div className="space-y-0.5">
            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
              พักสายตา / ไม่อยู่หน้าจอ (Idle)
            </p>
            <div className="flex items-baseline gap-1.5">
              <span className="text-xl font-bold text-amber-600">{stats.idle}</span>
              <span className="text-xs text-slate-500">คน</span>
            </div>
            <p className="text-[11px] text-amber-600 flex items-center gap-1 font-medium">
              <AlertTriangle className="w-3 h-3" />
              ไม่มีการขยับเกิน 3-4 นาที
            </p>
          </div>
          <div className="p-2.5 bg-amber-50 border border-amber-100 rounded-md text-amber-600">
            <AlertTriangle className="w-4 h-4" />
          </div>
        </div>

        {/* 3. System Logs Count */}
        <div className="bg-white border border-slate-200/90 rounded-md p-3 flex items-center justify-between shadow-sm">
          <div className="space-y-0.5">
            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
              บันทึกการทำงานล่าสุด (Logs)
            </p>
            <div className="flex items-baseline gap-1.5">
              <span className="text-xl font-bold text-blue-600">{systemLogs.length}</span>
              <span className="text-xs text-slate-500">รายการสด</span>
            </div>
            <p className="text-[11px] text-blue-600 font-medium flex items-center gap-1">
              <FileText className="w-3 h-3" />
              Action Logs จาก Firestore
            </p>
          </div>
          <div className="p-2.5 bg-blue-50 border border-blue-100 rounded-md text-blue-600">
            <FileText className="w-4 h-4" />
          </div>
        </div>

        {/* 4. Active Workspaces */}
        <div className="bg-white border border-slate-200/90 rounded-md p-3 flex items-center justify-between shadow-sm">
          <div className="space-y-0.5">
            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
              โมดูลที่มีการใช้งาน
            </p>
            <div className="flex items-baseline gap-1.5">
              <span className="text-xl font-bold text-indigo-600">{stats.topPages.length}</span>
              <span className="text-xs text-slate-500">หน้าต่างหลัก</span>
            </div>
            <p className="text-[11px] text-indigo-600 font-medium flex items-center gap-1 truncate max-w-[170px]">
              <Layers className="w-3 h-3 flex-shrink-0" />
              {stats.topPages[0] ? getFriendlyRouteName(stats.topPages[0][0]) : "ไม่มีผู้ใช้งาน"}
            </p>
          </div>
          <div className="p-2.5 bg-indigo-50 border border-indigo-100 rounded-md text-indigo-600">
            <Compass className="w-4 h-4" />
          </div>
        </div>
      </div>

      {/* Middle Grid: Top Active Pages & Live Action Stream Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-start">
        {/* 1. Top Active Pages Table Card */}
        <div className="bg-white border border-slate-200/90 rounded-md p-3 space-y-2.5 shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-100 pb-1.5">
            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
              <Layers className="w-4 h-4 text-rose-500" />
              หน้าต่างที่มีผู้ใช้งานมากที่สุด (Top Active Pages)
            </h3>
            <span className="text-[11px] text-slate-400 font-mono">
              {stats.topPages.length} หน้าต่าง
            </span>
          </div>

          <div className="overflow-x-auto max-h-48 overflow-y-auto border border-slate-100 rounded-md">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-slate-600 uppercase text-[10px] font-semibold tracking-wider sticky top-0">
                  <th className="py-1.5 px-2.5 w-8 text-center bg-slate-50">#</th>
                  <th className="py-1.5 px-2.5 bg-slate-50">ชื่อหน้า / โมดูล</th>
                  {/* <th className="py-1.5 px-2.5 hidden sm:table-cell bg-slate-50">สัดส่วน</th> */}
                  <th className="py-1.5 px-2.5 text-right bg-slate-50">ผู้ใช้งาน</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {stats.topPages.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="py-4 text-center text-slate-400 text-xs">
                      ไม่มีผู้ใช้งานที่ออนไลน์
                    </td>
                  </tr>
                ) : (
                  stats.topPages.map(([path, count], index) => {
                    const percentage = Math.round((count / (stats.online || 1)) * 100);
                    return (
                      <tr key={path} className="hover:bg-slate-50/80 transition-colors">
                        <td className="py-1.5 px-2.5 text-center font-mono text-[11px] text-slate-400">
                          {index + 1}
                        </td>
                        <td className="py-1.5 px-2.5">
                          <div className="font-semibold text-slate-800 truncate max-w-[200px]" title={path}>
                            {getFriendlyRouteName(path)}
                          </div>
                          <div className="text-[10px] text-slate-400 font-mono truncate max-w-[200px]">
                            {path}
                          </div>
                        </td>
                        {/* <td className="py-1.5 px-2.5 hidden sm:table-cell w-32">
                          <div className="flex items-center gap-1.5">
                            <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                              <div
                                className="bg-emerald-500 h-1.5 rounded-full transition-all duration-500"
                                style={{ width: `${percentage}%` }}
                              ></div>
                            </div>
                            <span className="text-[10px] font-mono text-slate-500 shrink-0 w-7 text-right">
                              {percentage}%
                            </span>
                          </div>
                        </td> */}
                        <td className="py-1.5 px-2.5 text-right">
                          <span className="inline-flex items-center font-mono text-emerald-600 font-bold bg-emerald-50 px-1.5 py-0.2 rounded border border-emerald-200 text-xs">
                            {count} คน
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* 2. Live Action Stream Card */}
        <div className="bg-white border border-slate-200/90 rounded-md p-3 space-y-2.5 shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-100 pb-1.5">
            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
              <Activity className="w-4 h-4 text-rose-500" />
              Live Action Stream (รายการทำงานสด)
            </h3>
            <span className="text-[10px] text-rose-600 font-semibold bg-rose-50 px-1.5 py-0.2 rounded border border-rose-200">
              เรียลไทม์
            </span>
          </div>

          <div className="space-y-1 max-h-48 overflow-y-auto pr-1">
            {systemLogs.length === 0 ? (
              <p className="text-xs text-slate-400 py-4 text-center">
                กำลังรอรับรายการทำงานใหม่จากระบบ...
              </p>
            ) : (
              systemLogs.slice(0, 10).map((log) => {
                const typeBadge = getTypeBadge(log.type);
                return (
                  <div
                    key={log.id}
                    onClick={() => setSelectedLogDetail(log)}
                    className="p-1.5 px-2 bg-slate-50 hover:bg-slate-100 border border-slate-200/80 rounded-md flex items-center justify-between gap-2 text-[11px] cursor-pointer transition-colors"
                  >
                    <div className="flex items-center gap-1.5 min-w-0 flex-1">
                      <span className="font-semibold text-slate-800 truncate max-w-[120px]">
                        {log.name || "Admin"}
                      </span>
                      <span className="text-[10px] px-1.5 py-0.2 rounded bg-white border border-slate-200 text-slate-600 font-medium shrink-0">
                        {typeBadge.label.split(" ")[0]}
                      </span>
                      <span className="text-rose-600 font-mono font-semibold truncate text-[10px]">
                        {log.action || "-"}
                      </span>
                    </div>
                    <span className="text-[10px] text-slate-400 font-mono shrink-0">
                      {formatTimeAgo(log.createdAt)}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Main Full-Width Section: Navigation Tabs & Full Tables */}
      <div className="w-full space-y-2.5">
        {/* Navigation Tabs */}
        <div className="flex items-center gap-2 border-b border-slate-200 pb-1.5">
          <button
            onClick={() => setActiveTab("presence")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-colors ${activeTab === "presence"
              ? "bg-rose-50 text-rose-600 border border-rose-200 shadow-xs"
              : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
              }`}
          >
            <Users className="w-3.5 h-3.5" />
            <span>สถานะแอดมิน & ประวัติเข้าหน้า (Presence & Page Visited)</span>
            <span className="px-1.5 py-0.2 rounded-md bg-white border border-slate-200 text-[10px] text-slate-600 font-mono">
              {admins.length}
            </span>
          </button>

          <button
            onClick={() => setActiveTab("logs")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-colors ${activeTab === "logs"
              ? "bg-rose-50 text-rose-600 border border-rose-200 shadow-xs"
              : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
              }`}
          >
            <FileText className="w-3.5 h-3.5" />
            <span>ประวัติการทำงานของระบบ (System Logs)</span>
            <span className="px-1.5 py-0.2 rounded-md bg-white border border-slate-200 text-[10px] text-slate-600 font-mono">
              {systemLogs.length}
            </span>
          </button>
        </div>

        {/* Filters Bar */}
        <div className="bg-white border border-slate-200 rounded-md p-2.5 flex flex-col sm:flex-row items-center justify-between gap-2.5 shadow-sm">
          {/* Search Input */}
          <div className="relative w-full sm:w-80">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={
                activeTab === "presence"
                  ? "ค้นหาชื่อ, รหัสแอดมิน, หน้าที่เปิด..."
                  : "ค้นหาผู้ทำรายการ, Action, หมวดหมู่, IP, Target..."
              }
              className="w-full pl-8 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-md text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:bg-white focus:border-rose-400 transition-colors"
            />
          </div>

          {/* Dynamic Filter Group depending on Active Tab */}
          {activeTab === "presence" ? (
            <div className="flex items-center gap-2 w-full sm:w-auto overflow-x-auto">
              {/* Status Filter */}
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="bg-slate-50 border border-slate-200 text-slate-700 text-xs rounded-md px-2.5 py-1.5 focus:outline-none focus:border-rose-400"
              >
                <option value="all">สถานะทั้งหมด</option>
                <option value="online">🟢 ออนไลน์ ({stats.online})</option>
                <option value="idle">🟡 พักสายตา ({stats.idle})</option>
                <option value="offline">⚪ ออฟไลน์ ({stats.offline})</option>
              </select>

              {/* Role Filter */}
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="bg-slate-50 border border-slate-200 text-slate-700 text-xs rounded-md px-2.5 py-1.5 focus:outline-none focus:border-rose-400"
              >
                <option value="all">ตำแหน่งทั้งหมด</option>
                {uniqueRoles.map((role) => (
                  <option key={role} value={role}>
                    {getRoleBadge(role).label}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="flex items-center gap-2 w-full sm:w-auto overflow-x-auto">
              {/* Log Type Filter */}
              <select
                value={selectedLogType}
                onChange={(e) => setSelectedLogType(e.target.value)}
                className="bg-slate-50 border border-slate-200 text-slate-700 text-xs rounded-md px-2.5 py-1.5 focus:outline-none focus:border-rose-400"
              >
                <option value="all">หมวดหมู่ทั้งหมด ({systemLogs.length})</option>
                {uniqueLogTypes.map((type) => (
                  <option key={type} value={type}>
                    {getTypeBadge(type).label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* TAB 1: Live Presence & Page History Table */}
        {activeTab === "presence" && (
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-slate-600 uppercase tracking-wider font-semibold">
                    <th className="py-2 px-3">แอดมิน / เจ้าหน้าที่</th>
                    <th className="py-2 px-3">ตำแหน่ง</th>
                    <th className="py-2 px-3">สถานะ</th>
                    <th className="py-2 px-3">หน้าที่กำลังเปิดอยู่</th>
                    <th className="py-2 px-3">อุปกรณ์ & ตำแหน่ง (Location)</th>
                    <th className="py-2 px-3 text-right">ประวัติ & การใช้งาน</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="py-8 text-center text-slate-400">
                        <div className="flex flex-col items-center justify-center gap-2">
                          <RefreshCw className="w-5 h-5 animate-spin text-rose-500" />
                          <span>กำลังเชื่อมต่อฐานข้อมูล Realtime...</span>
                        </div>
                      </td>
                    </tr>
                  ) : filteredAdmins.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="py-8 text-center text-slate-400">
                        <div className="flex flex-col items-center justify-center gap-2">
                          <Users className="w-6 h-6 text-slate-300" />
                          <span>ไม่พบข้อมูลแอดมินตามเงื่อนไขการค้นหา</span>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredAdmins.map((admin) => {
                      const isMe = Boolean(currentUserId && String(admin.userId || admin.id) === currentUserId);
                      const status = calculateEffectiveStatus(admin);
                      const roleInfo = getRoleBadge(admin.role);
                      const friendlyRoute = getFriendlyRouteName(admin.currentPath);
                      const initial = (admin.firstName || admin.name || "A").charAt(0).toUpperCase();
                      const visitCount = admin.recentVisits?.length || 0;

                      return (
                        <tr
                          key={admin.id || admin.userId}
                          className={`transition-colors ${isMe
                            ? "bg-rose-50/50 hover:bg-rose-50/80 border-l-4 border-l-rose-500"
                            : "hover:bg-slate-50/80"
                            }`}
                        >
                          {/* 1. Admin Info */}
                          <td className="py-2 px-3">
                            <div className="flex items-center gap-2.5">
                              <div className="relative flex-shrink-0">
                                {admin.profileImage ? (
                                  <img
                                    src={admin.profileImage}
                                    alt=""
                                    className="w-7 h-7 rounded-md bg-slate-100 object-cover border border-slate-200"
                                  />
                                ) : (
                                  <div className="w-7 h-7 rounded-md bg-slate-100 border border-slate-200 flex items-center justify-center font-bold text-xs text-slate-700">
                                    {initial}
                                  </div>
                                )}
                                {status === "online" && (
                                  <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 bg-emerald-500 border border-white rounded-full"></span>
                                )}
                                {status === "idle" && (
                                  <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 bg-amber-500 border border-white rounded-full"></span>
                                )}
                              </div>
                              <div className="min-w-0">
                                <p className="font-semibold text-slate-900 truncate flex items-center gap-1.5 text-xs">
                                  {admin.name || "Admin"}
                                  {isMe && (
                                    <span className="inline-flex items-center gap-0.5 px-1 py-0.2 rounded bg-rose-100 text-rose-700 border border-rose-200 text-[9px] font-bold">
                                      <Sparkles className="w-2.5 h-2.5" />
                                      คุณ (Dev)
                                    </span>
                                  )}
                                  {admin.userId && (
                                    <span className="text-[10px] text-slate-400 font-mono">
                                      #{admin.userId}
                                    </span>
                                  )}
                                </p>
                                <p className="text-[10px] text-slate-500 truncate flex items-center gap-1 font-mono">
                                  <Phone className="w-2.5 h-2.5 text-slate-400 flex-shrink-0" />
                                  <span>{admin.phone || admin.tel || "ไม่มีเบอร์โทร"}</span>
                                </p>
                                {(admin.lineTitle || admin.lineName) && (
                                  <p className="text-[10px] text-emerald-700 font-medium truncate flex items-center gap-1">
                                    <span className="font-bold text-[8px] px-1 py-0.2 bg-emerald-100 text-emerald-800 rounded">LINE</span>
                                    <span className="truncate">{admin.lineTitle || admin.lineName}</span>
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>

                          {/* 2. Role */}
                          <td className="py-2 px-3">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium border ${roleInfo.bg}`}
                            >
                              {roleInfo.label}
                            </span>
                          </td>

                          {/* 3. Status */}
                          <td className="py-2 px-3">
                            {status === "online" && (
                              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium text-[11px]">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                                Online
                              </span>
                            )}
                            {status === "idle" && (
                              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200 font-medium text-[11px]">
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                                Idle / พัก
                              </span>
                            )}
                            {status === "offline" && (
                              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 border border-slate-200 text-[11px]">
                                <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span>
                                Offline
                              </span>
                            )}
                          </td>

                          {/* 4. Current Path */}
                          <td className="py-2 px-3 max-w-xs">
                            <div className="space-y-0.5">
                              <div className="flex items-center gap-1 text-slate-800 font-medium truncate">
                                <span className="truncate">{friendlyRoute}</span>
                              </div>
                              <div className="text-[10px] text-slate-400 font-mono truncate">
                                {admin.currentPath || "/"}
                              </div>
                            </div>
                          </td>

                          {/* 5. Device & Location */}
                          <td className="py-2 px-3 text-slate-600">
                            <div className="space-y-0.5">
                              <div className="flex items-center gap-1 text-slate-700 font-medium">
                                <Laptop className="w-3 h-3 text-slate-400 flex-shrink-0" />
                                <span className="truncate">{admin.device || "ไม่ทราบอุปกรณ์"}</span>
                              </div>
                              <div className="flex items-center gap-1 text-[10px] text-slate-500 font-mono flex-wrap">
                                <MapPin className="w-2.5 h-2.5 text-rose-500 flex-shrink-0" />
                                <span className="truncate max-w-[140px]" title={admin.location || "กำลังระบุตำแหน่ง"}>
                                  {admin.city || admin.location || "กำลังระบุตำแหน่ง..."}
                                  {admin.isp ? ` (${admin.isp.split(" ")[0]})` : ""}
                                </span>
                                {admin.geo?.isGPS ? (
                                  <span className="inline-flex items-center gap-0.5 px-1 py-0.2 rounded bg-emerald-100 text-emerald-800 text-[8px] font-bold" title="พิกัด GPS แม่นยำจาก Browser">
                                    <Crosshair className="w-2 h-2" />
                                    GPS
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-0.5 px-1 py-0.2 rounded bg-sky-100 text-sky-800 text-[8px] font-bold" title="ตำแหน่งจาก IP Address">
                                    <Globe className="w-2 h-2" />
                                    IP
                                  </span>
                                )}
                                {admin.geo?.latitude && admin.geo?.longitude && (
                                  <a
                                    href={`https://www.google.com/maps?q=${admin.geo.latitude},${admin.geo.longitude}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-rose-600 hover:text-rose-800 hover:underline flex items-center gap-0.5 text-[9px]"
                                    title="เปิดดูตำแหน่งบน Google Maps"
                                  >
                                    <ExternalLink className="w-2 h-2" />
                                    <span>Map</span>
                                  </a>
                                )}
                              </div>
                            </div>
                          </td>

                          {/* 6. Page History Action & Last Seen */}
                          <td className="py-2 px-3 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => setSelectedAdminForHistory(admin)}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 text-xs font-semibold transition-colors"
                                title="ดูประวัติการเข้าหน้าของแอดมินคนนี้"
                              >
                                <History className="w-3.5 h-3.5" />
                                <span>ประวัติเข้าหน้า ({visitCount})</span>
                              </button>
                              <span className="text-slate-500 font-mono text-[10px] hidden sm:inline">
                                {formatTimeAgo(admin.lastActive, admin.lastActiveMs)}
                              </span>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* TAB 2: System Action Logs Table */}
        {activeTab === "logs" && (
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-slate-600 uppercase tracking-wider font-semibold">
                    <th className="py-2 px-3">วันเวลา (Timestamp)</th>
                    <th className="py-2 px-3">ผู้ทำรายการ</th>
                    <th className="py-2 px-3">หมวดหมู่ (Type)</th>
                    <th className="py-2 px-3">คำสั่ง (Action)</th>
                    <th className="py-2 px-3">IP / Target</th>
                    <th className="py-2 px-3 text-right">ดูข้อมูล</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {logsLoading ? (
                    <tr>
                      <td colSpan="6" className="py-8 text-center text-slate-400">
                        <div className="flex flex-col items-center justify-center gap-2">
                          <RefreshCw className="w-5 h-5 animate-spin text-rose-500" />
                          <span>กำลังโหลดรายการบันทึกการทำงาน...</span>
                        </div>
                      </td>
                    </tr>
                  ) : filteredLogs.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="py-8 text-center text-slate-400">
                        <div className="flex flex-col items-center justify-center gap-2">
                          <FileText className="w-6 h-6 text-slate-300" />
                          <span>ไม่พบบันทึกการทำงานตามเงื่อนไขการค้นหา</span>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredLogs.map((log) => {
                      const typeInfo = getTypeBadge(log.type);
                      const timeAgo = formatTimeAgo(log.createdAt);
                      const dateStr = log.createdAt
                        ? new Date(log.createdAt).toLocaleString("th-TH")
                        : "ไม่ระบุ";

                      return (
                        <tr
                          key={log.id}
                          className="hover:bg-slate-50/80 transition-colors group cursor-pointer"
                          onClick={() => setSelectedLogDetail(log)}
                        >
                          {/* 1. Timestamp */}
                          <td className="py-2 px-3 whitespace-nowrap">
                            <div className="space-y-0.5">
                              <div className="font-semibold text-slate-800">{timeAgo}</div>
                              <div className="text-[10px] text-slate-400 font-mono">{dateStr}</div>
                            </div>
                          </td>

                          {/* 2. Actor */}
                          <td className="py-2 px-3">
                            <div className="font-semibold text-slate-900 flex items-center gap-1.5">
                              <span>{log.name || "Admin"}</span>
                              {log.actor && (
                                <span className="text-[10px] text-slate-400 font-mono">
                                  #{log.actor}
                                </span>
                              )}
                            </div>
                          </td>

                          {/* 3. Type */}
                          <td className="py-2 px-3">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium border ${typeInfo.bg}`}
                            >
                              {typeInfo.label}
                            </span>
                          </td>

                          {/* 4. Action */}
                          <td className="py-2 px-3">
                            <span className="font-mono text-rose-600 font-semibold bg-rose-50/80 px-2 py-0.5 rounded-md border border-rose-200/60 text-[11px]">
                              {log.action || "-"}
                            </span>
                          </td>

                          {/* 5. IP & Target / Summary */}
                          <td className="py-2 px-3 max-w-xs truncate text-slate-600">
                            <div className="space-y-0.5">
                              {log.ip && (
                                <div className="flex items-center gap-1 text-[10px] font-mono text-slate-500">
                                  <Globe className="w-2.5 h-2.5 text-slate-400" />
                                  <span>{log.ip}</span>
                                </div>
                              )}
                              {log.type === "search" ? (
                                <div className="text-sky-800 font-semibold truncate text-[11px] flex items-center gap-1.5" title={log.detail || log.searchTerm || ""}>
                                  <Search className="w-3 h-3 text-sky-500 flex-shrink-0" />
                                  <span className="truncate">{log.detail || `ค้นหา: "${log.searchTerm}"`}</span>
                                </div>
                              ) : log.type === "navigation" ? (
                                <div className="text-violet-800 font-medium truncate text-[11px] flex items-center gap-1.5" title={log.detail || log.page || ""}>
                                  <Compass className="w-3 h-3 text-violet-500 flex-shrink-0" />
                                  <span className="truncate">{log.detail || `เข้าหน้า: ${log.page}`}</span>
                                </div>
                              ) : (
                                <div className="font-mono text-slate-700 truncate text-[11px]" title={log.detail || log.target || log.page || ""}>
                                  {log.detail || (log.target ? `Target: ${log.target}` : log.key ? `${log.key}: ${log.value || "-"}` : "-")}
                                </div>
                              )}
                            </div>
                          </td>

                          {/* 6. Action Button */}
                          <td className="py-2 px-3 text-right">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedLogDetail(log);
                              }}
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-slate-50 hover:bg-slate-100 text-slate-600 hover:text-slate-900 border border-slate-200 text-xs font-medium transition-colors"
                            >
                              <Eye className="w-3.5 h-3.5" />
                              <span>ดูข้อมูล</span>
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* 1. Modal: Admin Page Visit Navigation History (ประวัติการเข้าหน้าของแอดมิน) */}
      {selectedAdminForHistory && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-md shadow-2xl border border-slate-200 w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
            {/* Modal Header */}
            <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
              <div className="flex items-center gap-2.5">
                <History className="w-5 h-5 text-rose-600" />
                <div>
                  <h3 className="font-bold text-slate-900 text-sm">
                    ประวัติการเข้าหน้าของ {selectedAdminForHistory.name || "Admin"}
                  </h3>
                  <p className="text-[11px] text-slate-500">
                    User ID: #{selectedAdminForHistory.userId || selectedAdminForHistory.id} • เบอร์โทร: {selectedAdminForHistory.phone || selectedAdminForHistory.tel || "ไม่มีเบอร์โทร"}
                    {(selectedAdminForHistory.lineTitle || selectedAdminForHistory.lineName) ? ` • LINE: ${selectedAdminForHistory.lineTitle || selectedAdminForHistory.lineName}` : ""}
                    {" "}• อุปกรณ์: {selectedAdminForHistory.device || "Desktop"}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedAdminForHistory(null)}
                className="p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-200 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body: Timeline */}
            <div className="p-5 space-y-4 overflow-y-auto text-xs">
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-md flex items-center justify-between">
                <div>
                  <span className="text-[11px] text-slate-500 font-semibold block">หน้าที่กำลังเปิดอยู่ในปัจจุบัน:</span>
                  <span className="font-bold text-slate-900 text-xs flex items-center gap-1.5 mt-0.5">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                    {getFriendlyRouteName(selectedAdminForHistory.currentPath)}
                  </span>
                  <span className="text-[10px] text-slate-400 font-mono block">
                    {selectedAdminForHistory.currentPath || "/"}
                  </span>
                </div>
                <div className="text-right">
                  <span className="text-[10px] text-slate-400 block">อัปเดตล่าสุด</span>
                  <span className="font-mono text-slate-700 text-xs font-semibold">
                    {formatTimeAgo(selectedAdminForHistory.lastActive, selectedAdminForHistory.lastActiveMs)}
                  </span>
                </div>
              </div>

              {/* Geolocation & Network Info Card */}
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-md flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-slate-500 font-semibold flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5 text-rose-500" />
                      ตำแหน่งที่อยู่ (Geolocation) & เครือข่าย:
                    </span>
                    {selectedAdminForHistory.geo?.isGPS ? (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.2 rounded-md bg-emerald-100 text-emerald-800 text-[10px] font-bold">
                        <Crosshair className="w-2.5 h-2.5" />
                        GPS แม่นยำ
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.2 rounded-md bg-sky-100 text-sky-800 text-[10px] font-bold">
                        <Globe className="w-2.5 h-2.5" />
                        IP Location
                      </span>
                    )}
                  </div>
                  <div className="font-bold text-slate-800 text-xs">
                    {selectedAdminForHistory.location || selectedAdminForHistory.city || "ไทย (Thailand)"}
                  </div>
                  <div className="text-[11px] text-slate-500 font-mono flex items-center gap-2 flex-wrap">
                    {selectedAdminForHistory.ip && <span>IP: {selectedAdminForHistory.ip}</span>}
                    {selectedAdminForHistory.isp && <span>• ISP: {selectedAdminForHistory.isp}</span>}
                  </div>
                </div>
                {selectedAdminForHistory.geo?.latitude && selectedAdminForHistory.geo?.longitude && (
                  <a
                    href={`https://www.google.com/maps?q=${selectedAdminForHistory.geo.latitude},${selectedAdminForHistory.geo.longitude}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-white hover:bg-slate-100 text-slate-700 border border-slate-200 text-xs font-semibold shadow-xs transition-colors self-start sm:self-auto"
                  >
                    <ExternalLink className="w-3.5 h-3.5 text-rose-600" />
                    <span>ดูบน Google Maps</span>
                  </a>
                )}
              </div>

              {/* Timeline Items */}
              <div className="space-y-2">
                <h4 className="font-semibold text-slate-700 text-xs flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5 text-rose-500" />
                  ลำดับเส้นทางการเปิดหน้าต่างย้อนหลัง (Navigation Trail):
                </h4>

                {(!selectedAdminForHistory.recentVisits || selectedAdminForHistory.recentVisits.length === 0) ? (
                  <div className="p-8 text-center text-slate-400 border border-dashed border-slate-200 rounded-md">
                    <History className="w-6 h-6 mx-auto mb-2 text-slate-300" />
                    <span>ยังไม่มีบันทึกประวัติการเปลี่ยนหน้าสำหรับแอดมินท่านนี้</span>
                  </div>
                ) : (
                  <div className="space-y-2 relative before:absolute before:left-3.5 before:top-2 before:bottom-2 before:w-0.5 before:bg-slate-200 pl-8">
                    {selectedAdminForHistory.recentVisits.map((visit, idx) => {
                      const friendly = getFriendlyRouteName(visit.path);
                      const timeStr = visit.timeFormatted || (visit.timestamp ? new Date(visit.timestamp).toLocaleTimeString("th-TH") : "-");
                      const isCurrent = idx === 0;

                      return (
                        <div key={idx} className="relative group">
                          {/* Dot indicator */}
                          <div
                            className={`absolute -left-[27px] top-1.5 w-3 h-3 rounded-full border-2 border-white ${isCurrent ? "bg-rose-500 ring-2 ring-rose-200" : "bg-slate-300"
                              }`}
                          ></div>

                          <div className="p-2.5 bg-slate-50 hover:bg-slate-100/80 border border-slate-200 rounded-md flex items-center justify-between gap-3 transition-colors">
                            <div className="space-y-0.5 min-w-0">
                              <div className="font-semibold text-slate-800 flex items-center gap-1.5">
                                <span className="truncate">{friendly}</span>
                                {isCurrent && (
                                  <span className="px-1.5 py-0.2 rounded-md bg-rose-100 text-rose-700 text-[9px] font-bold">
                                    ล่าสุด
                                  </span>
                                )}
                              </div>
                              <div className="text-[10px] text-slate-400 font-mono truncate">
                                {visit.path}
                              </div>
                            </div>

                            <div className="text-right flex-shrink-0">
                              <span className="text-[11px] font-mono text-slate-600 block">{timeStr}</span>
                              <span className="text-[10px] text-slate-400 block">
                                {formatTimeAgo(visit.timestamp)}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-3 border-t border-slate-200 flex justify-end bg-slate-50">
              <button
                onClick={() => setSelectedAdminForHistory(null)}
                className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-md font-semibold text-xs transition-colors"
              >
                ปิดหน้าต่าง
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 2. Modal: Log Detail Modal (Raw Payload & Context) */}
      {selectedLogDetail && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-md shadow-2xl border border-slate-200 w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
            {/* Modal Header */}
            <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-rose-600" />
                <h3 className="font-bold text-slate-900 text-sm">
                  รายละเอียดบันทึกการทำงาน (Log Detail)
                </h3>
              </div>
              <button
                onClick={() => setSelectedLogDetail(null)}
                className="p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-200 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5 space-y-4 overflow-y-auto text-xs">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-md">
                  <span className="text-slate-400 text-[10px] block">วันเวลา</span>
                  <span className="font-semibold text-slate-800">
                    {selectedLogDetail.createdAt
                      ? new Date(selectedLogDetail.createdAt).toLocaleString("th-TH")
                      : "-"}
                  </span>
                </div>
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-md">
                  <span className="text-slate-400 text-[10px] block">ผู้ทำรายการ</span>
                  <span className="font-semibold text-slate-800">
                    {selectedLogDetail.name || "Admin"} ({selectedLogDetail.actor || "-"})
                  </span>
                </div>
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-md">
                  <span className="text-slate-400 text-[10px] block">หมวดหมู่</span>
                  <span className="font-semibold text-slate-800">{selectedLogDetail.type || "-"}</span>
                </div>
              </div>

              {/* IP & User Agent Info */}
              {(selectedLogDetail.ip || selectedLogDetail.userAgent) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-md">
                    <span className="text-slate-400 text-[10px] block flex items-center gap-1">
                      <Globe className="w-3 h-3 text-slate-400" />
                      Client IP Address
                    </span>
                    <span className="font-mono text-slate-800 font-semibold text-xs">
                      {selectedLogDetail.ip || "ไม่ระบุ"}
                    </span>
                  </div>
                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-md">
                    <span className="text-slate-400 text-[10px] block flex items-center gap-1">
                      <Laptop className="w-3 h-3 text-slate-400" />
                      User Agent
                    </span>
                    <span className="font-mono text-slate-600 text-[10px] truncate block" title={selectedLogDetail.userAgent}>
                      {selectedLogDetail.userAgent || "ไม่ระบุ"}
                    </span>
                  </div>
                </div>
              )}

              {/* Search Query Details Card (เมื่อเป็นหมวดหมู่ Search) */}
              {selectedLogDetail.type === "search" && (
                <div className="p-3.5 bg-sky-50 border border-sky-200 rounded-md space-y-1.5">
                  <span className="text-sky-800 font-bold text-xs flex items-center gap-1.5">
                    <Search className="w-4 h-4 text-sky-600" />
                    คำค้นหาที่ผู้ใช้ระบุ (Search Keyword):
                  </span>
                  <div className="text-slate-900 font-bold text-sm bg-white p-2.5 rounded border border-sky-100 font-mono">
                    "{selectedLogDetail.searchTerm || selectedLogDetail.target || "-"}"
                  </div>
                  <div className="text-[11px] text-sky-700 flex items-center gap-3 flex-wrap">
                    <span>หน้าต่าง: <b>{selectedLogDetail.page || "-"}</b></span>
                    {selectedLogDetail.searchType && <span>• ตัวกรอง: <b>{selectedLogDetail.searchType}</b></span>}
                    {selectedLogDetail.detail && <span>• สรุป: {selectedLogDetail.detail}</span>}
                  </div>
                </div>
              )}

              {/* Navigation Details Card (เมื่อเป็นหมวดหมู่ Navigation) */}
              {selectedLogDetail.type === "navigation" && (
                <div className="p-3.5 bg-violet-50 border border-violet-200 rounded-md space-y-1.5">
                  <span className="text-violet-800 font-bold text-xs flex items-center gap-1.5">
                    <Compass className="w-4 h-4 text-violet-600" />
                    หน้าเว็บที่เข้าชม (Page Visited):
                  </span>
                  <div className="text-slate-900 font-bold text-sm bg-white p-2.5 rounded border border-violet-100">
                    {selectedLogDetail.page || "-"}
                  </div>
                  <div className="text-[11px] text-violet-700 font-mono">
                    Path URL: <b>{selectedLogDetail.path || selectedLogDetail.target || "-"}</b>
                  </div>
                </div>
              )}

              <div className="p-3 bg-rose-50/50 border border-rose-200/80 rounded-md space-y-1">
                <span className="text-rose-700 font-semibold text-[11px] block">คำสั่ง / Action Endpoint</span>
                <span className="font-mono text-rose-600 font-bold text-xs">{selectedLogDetail.action || "-"}</span>
              </div>

              {/* Payload JSON Viewer (Raw Unmasked Data) */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500 font-semibold text-[11px] block">
                    ข้อมูลทั้งหมดที่ถูกส่งมา (Raw Payload & Data):
                  </span>
                  <span className="text-[10px] font-mono text-slate-400">JSON Format</span>
                </div>
                <pre className="p-3 bg-slate-900 text-emerald-400 font-mono text-[11px] rounded-md overflow-x-auto max-h-64 leading-relaxed">
                  {JSON.stringify(selectedLogDetail, null, 2)}
                </pre>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-3 border-t border-slate-200 flex justify-end bg-slate-50">
              <button
                onClick={() => setSelectedLogDetail(null)}
                className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-md font-semibold text-xs transition-colors"
              >
                ปิดหน้าต่าง
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DevMonitor;
